import { useMemo, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  DashboardPeriod,
  DashboardTaskMetric,
  DashboardWeeklyTasks,
} from '../../../services/dashboard';
import {
  TOP_CHART_CARD_HEIGHT,
  cardBodySx,
  cardHeaderSx,
  cardStatSx,
  cardSubtitleSx,
  cardTitleSx,
  chartCardSx,
  chartTooltipProps,
  dashboardTokens,
  dropdownSx,
  filledBarSx,
  ghostBarSx,
} from './dashboardCardStyles';

const CHART_HEIGHT = 160;
const BAR_WIDTH = 22;
const SLOT_HEIGHT = 130;
const Y_AXIS_WIDTH = 32;

type WeeklyTaskCardProps = {
  weeklyTasks: DashboardWeeklyTasks;
  loading?: boolean;
  onPeriodChange: (period: DashboardPeriod) => void;
  onMetricChange: (metric: DashboardTaskMetric) => void;
};

export const WeeklyTaskCard = ({
  weeklyTasks,
  loading = false,
  onPeriodChange,
  onMetricChange,
}: WeeklyTaskCardProps) => {
  const [periodAnchor, setPeriodAnchor] = useState<null | HTMLElement>(null);
  const [metricAnchor, setMetricAnchor] = useState<null | HTMLElement>(null);
  const { period, metric, series } = weeklyTasks;

  const { bars, peak, total, avg } = useMemo(() => {
    const peakVal = Math.max(...series.map((s) => s.rawValue), 0);
    const totalVal = series.reduce((sum, s) => sum + s.rawValue, 0);
    return {
      peak: peakVal,
      total: totalVal,
      avg: series.length ? Math.round(totalVal / series.length) : 0,
      bars: series.map((d, index) => ({
        ...d,
        fillHeight: peakVal > 0 ? Math.max((d.rawValue / peakVal) * SLOT_HEIGHT, d.rawValue > 0 ? 6 : 0) : 0,
        active: index % 2 === 0,
      })),
    };
  }, [series]);

  const metricLabel = metric === 'enquiries' ? 'Enquiries' : 'Bookings';
  const periodLabel = period === 'monthly' ? 'Monthly' : 'Weekly';
  const periodDesc =
    period === 'monthly' ? 'Grouped by week (W1–W4)' : 'Daily breakdown (Sun–Sat)';

  return (
    <Box sx={{ ...chartCardSx(TOP_CHART_CARD_HEIGHT), opacity: loading ? 0.65 : 1, transition: 'opacity 0.2s' }}>
      <Box sx={cardHeaderSx}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={cardTitleSx}>Weekly Task</Typography>
          <Typography sx={cardSubtitleSx}>
            {metricLabel} · {periodDesc}
          </Typography>
          <Typography sx={cardStatSx}>
            Total {total.toLocaleString('en-IN')} · Avg {avg}/day · Peak {peak}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
          <Box onClick={(e) => setMetricAnchor(e.currentTarget)} sx={dropdownSx}>
            {metricLabel}
            <KeyboardArrowDown sx={{ fontSize: 16, color: '#bbb' }} />
          </Box>
          <Box onClick={(e) => setPeriodAnchor(e.currentTarget)} sx={dropdownSx}>
            {periodLabel}
            <KeyboardArrowDown sx={{ fontSize: 16, color: '#bbb' }} />
          </Box>
        </Box>
      </Box>

      <Menu anchorEl={metricAnchor} open={Boolean(metricAnchor)} onClose={() => setMetricAnchor(null)}>
        <MenuItem
          selected={metric === 'bookings'}
          onClick={() => {
            onMetricChange('bookings');
            setMetricAnchor(null);
          }}
        >
          Bookings
        </MenuItem>
        <MenuItem
          selected={metric === 'enquiries'}
          onClick={() => {
            onMetricChange('enquiries');
            setMetricAnchor(null);
          }}
        >
          Enquiries
        </MenuItem>
      </Menu>

      <Menu anchorEl={periodAnchor} open={Boolean(periodAnchor)} onClose={() => setPeriodAnchor(null)}>
        <MenuItem
          selected={period === 'weekly'}
          onClick={() => {
            onPeriodChange('weekly');
            setPeriodAnchor(null);
          }}
        >
          Weekly
        </MenuItem>
        <MenuItem
          selected={period === 'monthly'}
          onClick={() => {
            onPeriodChange('monthly');
            setPeriodAnchor(null);
          }}
        >
          Monthly
        </MenuItem>
      </Menu>

      <Box sx={{ ...cardBodySx, position: 'relative', mt: 1.5 }}>
        <Box sx={{ position: 'relative', height: CHART_HEIGHT + 28, pl: `${Y_AXIS_WIDTH}px` }}>
          <Box
            sx={{
              position: 'absolute',
              left: Y_AXIS_WIDTH,
              right: 0,
              top: 0,
              height: CHART_HEIGHT - 28,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-around',
              px: 0.5,
            }}
          >
            {bars.map((d) => {
              const fillColor = d.rawValue > 0 ? (d.active ? '#ffc2d4' : '#86878c') : 'transparent';
              const tooltipTitle = (
                <Box sx={{ lineHeight: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{d.day}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#ccc' }}>
                    {metricLabel}: <strong>{d.rawValue.toLocaleString('en-IN')}</strong>
                  </Typography>
                  {peak > 0 && (
                    <Typography sx={{ fontSize: 11, color: '#888' }}>
                      {Math.round((d.rawValue / peak) * 100)}% of peak
                    </Typography>
                  )}
                </Box>
              );

              return (
                <Tooltip key={d.day} title={tooltipTitle} {...chartTooltipProps}>
                  <Box
                    sx={{
                      width: BAR_WIDTH,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'default',
                      '&:hover .task-ghost': { bgcolor: dashboardTokens.ghostBarHover },
                    }}
                  >
                    <Box sx={{ ...ghostBarSx(SLOT_HEIGHT, BAR_WIDTH), className: 'task-ghost' }}>
                      {d.rawValue > 0 && (
                        <Box sx={filledBarSx(d.fillHeight, fillColor)} />
                      )}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              left: Y_AXIS_WIDTH,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'space-around',
              px: 0.5,
            }}
          >
            {bars.map((d) => (
              <Typography
                key={d.day}
                sx={{
                  color: d.rawValue > 0 ? '#d9d9d9' : '#666',
                  fontSize: 12,
                  fontWeight: d.rawValue > 0 ? 600 : 500,
                  textAlign: 'center',
                  width: BAR_WIDTH + 6,
                }}
              >
                {d.day}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
